import Image from 'next/image'

import Modal from '@components/Modal'
import type { GiftProduct } from '@lib/types/get-started-offer'
import { FALLBACK_GIFT_PRODUCTS } from '@lib/types/get-started-offer'

export default function FreeGiftModal({
	isOpen,
	onClose,
	products: productsProp,
	totalValue,
	heading,
	subtext,
	disclaimer
}: {
	isOpen: boolean
	onClose: () => void
	products?: GiftProduct[]
	totalValue?: string
	heading?: string | null
	subtext?: string | null
	disclaimer?: string | null
}) {
	const products = productsProp?.length ? productsProp : FALLBACK_GIFT_PRODUCTS
	const displayValue = totalValue ?? '$200'

	return (
		<Modal isOpen={isOpen} closeModal={onClose} desktopStyle="floating" ariaLabel="Free gift details">
			<div className="px-6 pt-8 pb-6 max-w-lg w-full">
				{/* Header */}
				<div className="text-center mb-6">
					<span className="text-3xl" aria-hidden>🎁</span>
					<h2 className="mt-2 text-xl font-serif font-medium text-bbx-style">
						{heading ?? 'Your Free Gift'}
					</h2>
					<p className="mt-1 text-sm text-gray-500">
						{displayValue}+ {subtext ?? 'in luxury beauty products — included with your Annual plan'}
					</p>
				</div>

				{/* Product list */}
				<div className="flex flex-col divide-y divide-gray-100">
					{products.map((product, i) => (
						<div key={i} className="flex gap-4 py-4 first:pt-0 last:pb-0">
							{/* Product image */}
							<div className="relative w-20 h-20 shrink-0 bg-gray-50 rounded overflow-hidden">
								{product.image ? (
									<Image
										src={product.image}
										alt={`${product.brand} ${product.name}`}
										fill
										className="object-cover"
										sizes="80px"
									/>
								) : (
									<div className="w-full h-full flex items-center justify-center text-2xl">🎁</div>
								)}
							</div>

							{/* Product info */}
							<div className="flex flex-col justify-center min-w-0">
								<p className="text-xs font-semibold uppercase tracking-widest text-gray-400 truncate">
									{product.brand}
								</p>
								<p className="text-sm font-semibold text-bbx-style leading-snug mt-0.5">
									{product.name}
								</p>
								{product.description && (
									<p className="text-xs text-gray-500 mt-1 leading-snug line-clamp-2">
										{product.description}
									</p>
								)}
								{product.value && (
									<p className="text-xs font-bold text-gray-700 mt-1.5">
										Value: {product.value}
									</p>
								)}
							</div>
						</div>
					))}
				</div>

				{/* Footer note */}
				<p className="mt-5 text-xs text-center text-gray-400 leading-snug">
					{disclaimer ?? 'Free gift ships with your first Annual box. Subject to availability.'}
				</p>
			</div>
		</Modal>
	)
}
